import {
  ApiResponse,
  Subscription,
  SubscriptionListResponse,
  ChargeInquiry,
  ChargeInquiryListResponse,
  Invoice,
  InvoiceListResponse,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  GenerateChargeInquiriesRequest,
  GenerateChargesResponse,
  SubscriptionStatistics,
  SubscriptionFilters,
  ChargeInquiryFilters,
  InvoiceFilters,
  SubscriptionBillingRunListResponse,
} from '@mygate/common';
import { api } from './api';
import { useAuthStore } from '../stores/authStore';

class SubscriptionService {
  // Subscription Management
  async createSubscription(data: CreateSubscriptionRequest): Promise<ApiResponse<Subscription>> {
    return await api.post<Subscription>('/api/staff/subscriptions', data);
  }

  async updateSubscription(id: string, data: UpdateSubscriptionRequest): Promise<ApiResponse<Subscription>> {
    return await api.put<Subscription>(`/api/staff/subscriptions/${id}`, data);
  }

  async getSubscription(id: string): Promise<ApiResponse<Subscription>> {
    return await api.get<Subscription>(`/api/staff/subscriptions/${id}`);
  }

  async getUserSubscription(userId: string): Promise<ApiResponse<Subscription | null>> {
    return await api.get<Subscription | null>(`/api/staff/subscriptions/user/${userId}`);
  }

  async listSubscriptions(filters?: SubscriptionFilters): Promise<ApiResponse<SubscriptionListResponse>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    const endpoint = queryString ? `/api/staff/subscriptions?${queryString}` : '/api/staff/subscriptions';
    return await api.get<SubscriptionListResponse>(endpoint);
  }

  async cancelSubscription(id: string): Promise<ApiResponse<{ message: string }>> {
    return await api.delete<{ message: string }>(`/api/staff/subscriptions/${id}`);
  }

  async getStatistics(): Promise<ApiResponse<SubscriptionStatistics>> {
    return await api.get<SubscriptionStatistics>('/api/staff/subscriptions/statistics');
  }

  // Charge Inquiry Management
  async generateChargeInquiries(data: GenerateChargeInquiriesRequest): Promise<ApiResponse<GenerateChargesResponse>> {
    return await api.post<GenerateChargesResponse>('/api/staff/charge-inquiries/generate', data);
  }

  async listChargeInquiries(filters?: ChargeInquiryFilters): Promise<ApiResponse<ChargeInquiryListResponse>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    const endpoint = queryString ? `/api/staff/charge-inquiries?${queryString}` : '/api/staff/charge-inquiries';
    return await api.get<ChargeInquiryListResponse>(endpoint);
  }

  async getChargeInquiry(id: string): Promise<ApiResponse<ChargeInquiry>> {
    return await api.get<ChargeInquiry>(`/api/staff/charge-inquiries/${id}`);
  }

  async approveChargeInquiry(id: string, notes?: string): Promise<ApiResponse<ChargeInquiry>> {
    return await api.post<ChargeInquiry>(`/api/staff/charge-inquiries/${id}/approve`, { notes });
  }

  async bulkApproveCharges(
    chargeInquiryIds: string[],
  ): Promise<ApiResponse<{ approvedCount: number; errors: string[] }>> {
    return await api.post<{ approvedCount: number; errors: string[] }>('/api/staff/charge-inquiries/bulk-approve', {
      chargeInquiryIds,
    });
  }

  async cancelChargeInquiry(id: string, reason?: string): Promise<ApiResponse<{ message: string }>> {
    return await api.post<{ message: string }>(`/api/staff/charge-inquiries/${id}/cancel`, { reason });
  }

  async retryFailedCharges(): Promise<ApiResponse<{ retriedCount: number }>> {
    return await api.post<{ retriedCount: number }>('/api/staff/charge-inquiries/retry-failed');
  }

  // Auto-billing run journal
  async listBillingRuns(page: number, limit: number): Promise<ApiResponse<SubscriptionBillingRunListResponse>> {
    return await api.get<SubscriptionBillingRunListResponse>(
      `/api/staff/subscription-billing-runs?page=${page}&limit=${limit}`,
    );
  }

  // Invoice Management
  async listInvoices(filters?: InvoiceFilters): Promise<ApiResponse<InvoiceListResponse>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Convert Date objects to ISO strings for proper API serialization
          if (value instanceof Date) {
            params.append(key, value.toISOString());
          } else {
            params.append(key, String(value));
          }
        }
      });
    }
    const queryString = params.toString();
    const endpoint = queryString ? `/api/staff/invoices?${queryString}` : '/api/staff/invoices';
    return await api.get<InvoiceListResponse>(endpoint);
  }

  async getInvoice(id: string): Promise<ApiResponse<Invoice>> {
    return await api.get<Invoice>(`/api/staff/invoices/${id}`);
  }

  async resendInvoiceEmail(id: string): Promise<ApiResponse<{ message: string }>> {
    return await api.post<{ message: string }>(`/api/staff/invoices/${id}/resend`);
  }

  async regenerateInvoicePDF(id: string): Promise<ApiResponse<{ pdfUrl: string }>> {
    return await api.post<{ pdfUrl: string }>(`/api/staff/invoices/${id}/regenerate-pdf`);
  }

  /**
   * Download invoice PDF
   * Downloads the invoice as a PDF file directly to the user's browser
   */
  async downloadInvoicePDF(id: string): Promise<void> {
    try {
      // Replicate ApiClient URL construction pattern
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL || '';
      const url = `${baseUrl}/api/staff/invoices/${id}/download`;

      // Get token from authStore (same as ApiClient)
      const accessToken = useAuthStore.getState().accessToken;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download invoice PDF');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `invoice_${id}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  }

  /**
   * Export invoices as CSV
   * Downloads invoices as a CSV file with the specified filters
   */
  async exportInvoicesCsv(filters: { issuedDateFrom: string; issuedDateTo: string; status?: string }): Promise<void> {
    try {
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL || '';

      // Build query string
      const params = new URLSearchParams();
      params.append('issuedDateFrom', filters.issuedDateFrom);
      params.append('issuedDateTo', filters.issuedDateTo);
      if (filters.status) {
        params.append('status', filters.status);
      }

      const url = `${baseUrl}/api/staff/invoices/export/csv?${params.toString()}`;
      const accessToken = useAuthStore.getState().accessToken;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export invoices CSV');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `invoices_${filters.issuedDateFrom}_to_${filters.issuedDateTo}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting invoices CSV:', error);
      throw error;
    }
  }
}

export const subscriptionService = new SubscriptionService();
